import { NgClass } from '@angular/common';
import { Component, computed, inject, output, signal, viewChild } from '@angular/core';
import { SearchItemsComponent } from '../search-items/search-items.component';
import { ChartService } from 'src/app/services/chart/chart.service';
import { isChart } from 'src/app/shared/utils';
import { Chart } from 'src/app/types/chart';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatchingConditionType, MatchingData, MatchingOperator, MatchingProductConditon } from 'src/app/types/general';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { TableChart } from 'src/app/types/table-chart';
import { Category } from 'src/app/types/category';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { MatButtonModule } from '@angular/material/button';

@Component({
  templateUrl: './assigning.component.html',
  standalone: true,
  selector: 'app-onboarding-assigning',
  imports: [MatButtonModule, NgClass, SearchItemsComponent, TranslatePipe],
  styles: `
    button[mat-button],
    button[mat-flat-button] {
      --mdc-filled-button-container-height: 36px;
      --mdc-text-button-container-height: 36px;
    }
  `,
})
export class AssigningComponent extends BaseComponent {
  private readonly chartService = inject(ChartService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(false);

  readonly back = output<void>();
  readonly continue = output<void>();

  readonly selectedMethod = signal(-1);

  readonly continueDisabled = computed(() => this.selectedMethod() < 0 || this.selectedMethod() > 1);

  readonly searchComp = viewChild.required(SearchItemsComponent);

  onContinue() {
    if (this.continueDisabled()) return;

    const chart = this.chartService.chart;

    if (!chart) {
      this.continue.emit();
      return;
    }

    this.loading.set(true);

    let conditions: MatchingProductConditon[] = [];
    let matching: MatchingData | undefined = undefined;

    if (this.selectedMethod() === 0) {
      conditions = [{ type: 'all_products', value: 'allProducts', operation: MatchingOperator.exact }];
      matching = {
        matchType: 'any',
        conditions,
      };
    } else {
      const selectedItems = this.searchComp()
        .showingResult()
        .filter((item) => this.searchComp().selectedResults().includes(item.id));
      conditions = selectedItems.map<MatchingProductConditon>((item) => ({
        type: MatchingConditionType.products,
        operation: MatchingOperator.exact,
        value: item.id,
        item,
      }));

      matching = {
        matchType: 'any',
        conditions,
      };
    }

    if (isChart(chart)) {
      const sizeChart = chart as Chart;

      this.sub$ = this.chartService
        .updateChart(chart._id, {
          published: sizeChart.published,
          measurements: sizeChart.measurements,
          guide: sizeChart.guide,
          labels: sizeChart.labels,
          matching,
          model: sizeChart.model,
          sizeRecommender: sizeChart.sizeRecommender,
          optionNames: sizeChart.optionNames,
          category: (sizeChart.category as Category)._id,
          name: sizeChart.name,
          measurementVisibility: sizeChart.measurementVisibility,
          showUnitConversion: sizeChart.showUnitConversion,
        })
        .subscribe({
          next: () => {
            this.continue.emit();
            this.loading.set(false);
          },
          error: (error) => {
            captureException(error);
            this.snackbar.open(
              this.translate.instant(_('error.error-occured')),
              this.translate.instant(_('common.close')),
              { panelClass: 'snackbar-error' },
            );
            this.loading.set(false);
          },
        });
    } else {
      const chartTable = chart as TableChart;

      this.sub$ = this.chartService
        .updateTableChart(chart._id, {
          published: chartTable.published,
          matching: {
            matchType: 'any',
            conditions,
          },
          blocks: chartTable.blocks,
          tableData: chartTable.tableData,
          name: chartTable.name,
          heroImage: chartTable.heroImage,
          heroText: chartTable.heroText,
          tableFreeze: chartTable.tableFreeze,
          howToMeasure: chartTable.howToMeasure,
          showUnitConversion: chartTable.showUnitConversion,
          sizeRecommender: chartTable.sizeRecommender,
        })
        .subscribe({
          next: () => {
            this.continue.emit();
            this.loading.set(false);
          },
          error: (error) => {
            captureException(error);
            this.snackbar.open(
              this.translate.instant(_('error.error-occured')),
              this.translate.instant(_('common.close')),
              { panelClass: 'snackbar-error' },
            );
            this.loading.set(false);
          },
        });
    }
  }
}
